export type Franchisee = {
  af_number: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  marketing_name?: string | null;
  vanity_url?: string | null;
  site_url?: string | null;
  all_in_one_report?: string | null;
  gmb_link?: string | null;
  webform_emails?: string | null;
  active_ppc?: string | null;
  ppc_callrail?: string | null;
  ppc_lp?: string | null;
  city?: string | null;
  state?: string | null;
  territory_name?: string | null;
  fbc_name?: string | null;
  status?: string | null;
  // Satellite roll-up flags (computed server-side from Zoho + Yext).
  is_satellite?: boolean;
  parent_af?: string | null;
  has_own_office?: boolean;
  shared_satellites?: string[];
};

export type AdMatchTerritory = {
  afSub: string;
  matchAmount: number;
  /** Highest month of last 6 × 12 (col J). May be 0 for new franchisees. */
  annualized?: number;
  /** Monthly matched-lead cap for the Qualified Lead program (derived from annualized). */
  qualifiedLeads?: number;
};

export type QualifiedLeadProgram = {
  leadCost: number;        // A Place for Mom cost per lead ($58)
  matchRate: number;       // 0.5 (franchisor covers 50%)
  perLeadMatch: number;    // $29
  enrollContactName: string;
  enrollContactEmail: string;
  totalLeads: number;      // sum of per-territory caps
  maxMonthlyMatch: number; // totalLeads × perLeadMatch
};

export type AdMatchResponse = {
  af: string;
  found: boolean;
  name?: string;
  matchAmount?: number;
  combinedMatch?: number;
  territories?: AdMatchTerritory[];
  qualifiedLead?: QualifiedLeadProgram;
  source?: string;
  lastUpdated?: string;
};

export type AdAllocation = {
  afSub: string;
  afBase: string;
  franchiseeName: string | null;
  googlePct: number;
  metaPct: number;
  updatedBy: string | null;
  updatedAt: string | null;
};

export type AdAllocationListResponse = { allocations: AdAllocation[] };

export type SocialResponse = {
  af_number: string;
  facebook?: string | null;
  instagram?: string | null;
  youtube?: string | null;
  twitter?: string | null;
  site_url?: string | null;
  scraped_at?: string | null;
  refreshing?: boolean;
};

export type YextListing = {
  publisher: string;
  status: string;
  url?: string;
  screenshotUrl?: string;
};

export type ListingsResponse = {
  entity?: {
    id?: string;
    name?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      region?: string;
      postalCode?: string;
      countryCode?: string;
    };
  } | null;
  summary?: { live?: number; total?: number };
  listings?: YextListing[];
  refreshing?: boolean;
  search?: string;
};

export type Review = {
  authorName?: string;
  text?: string;
  rating?: number;
  relativeTime?: string;
  photoUri?: string;
};

export type ReviewsResponse = {
  af_number: string;
  placeId?: string;
  rating?: number;
  userRatingCount?: number;
  reviews?: Review[];
  synced_at?: string;
  refreshing?: boolean;
};

export type LocationAddressResponse = {
  af: string;
  zoho: {
    businessAddress: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    marketingName: string;
    territoryName: string;
    fullAddress: string;
  };
  yext: {
    fullAddress: string;
    entityId: string;
    name: string;
  };
  matchLevel: 'exact' | 'close' | 'zip' | 'none' | 'no-yext';
  match: boolean;
  typo: { field: 'city' | 'street'; zoho: string; yext: string } | null;
  hasZoho: boolean;
  hasYext: boolean;
};

export type AnalyticsKpis = {
  sessions: number;
  sessionsChange: number;
  newUsers: number;
  newUsersChange: number;
  totalUsers: number;
  totalUsersChange: number;
  engagedSessions: number;
  engagedSessionsChange: number;
  phoneClicks: number;
  phoneClicksChange: number;
  webformSubmissions: number;
  webformSubmissionsChange: number;
  chats: number;
  chatsChange: number;
};

export type AnalyticsResponse = {
  af_number: string;
  kpis: AnalyticsKpis;
  date_range?: string;
  page_path?: string;
  geoData?: Array<{ city: string; region: string; sessions: number }>;
  popularPages?: Array<{ title: string; newUsers: number; engagedSessions: number }>;
  trafficSources?: Array<{ channel: string; sessions: number }>;
  usersOverTime?: Array<{ week: string; newUsers: number }>;
  refreshing?: boolean;
};

export type RankingKeyword = {
  keyword: string;
  organicRank: number | null;
  inLocalPack: boolean;
  localPackRank: number | null;
};

export type RankingsResponse = {
  market: string;
  state: string;
  h1: string;
  queries: string[];
  keywords: RankingKeyword[];
  locationCode?: string | null;
  refreshing?: boolean;
  message?: string;
};

export type GbpLocation = {
  totalViews: number;
  viewsSearch: number;
  viewsMaps: number;
  activity: number;
  drivingDirections: number;
  phoneCalls: number;
  websiteClicks: number;
  totalViewsChange: number | null;
  viewsSearchChange: number | null;
  viewsMapsChange: number | null;
  activityChange: number | null;
  drivingDirectionsChange: number | null;
  phoneCallsChange: number | null;
  websiteClicksChange: number | null;
};

export type GbpSheetResponse = {
  location: GbpLocation | null;
  dateRange: string;
};

export type GbpNetworkMetric = {
  current: number;
  change: number | null;
  contributingLocations: number;
};

export type RankingsTopPerformer = {
  afNumber: string;
  name: string;
  location: string;
  topThreeCount: number;
  packCount: number;
  bestRank: number;
};

export type RankingsLaggard = {
  afNumber: string;
  name: string;
  location: string;
  daysOpen: number;
  openDate: string;
};

export type RankingsGridCell = {
  row: number;
  col: number;
  lat: number;
  lng: number;
  mapsRank: number | null;
  organicRank: number | null;
};

export type RankingsGridForKeyword = {
  keyword: string;
  cells: RankingsGridCell[];
};

export type RankingsGrid = {
  afNumber: string;
  centerLat: number | null;
  centerLng: number | null;
  gridSize: number;
  halfWidthKm: number;
  keywords: RankingsGridForKeyword[];
  checkedAt: string | null;
  refreshing?: boolean;
};

export type GbpHealthActionItem = {
  priority: 'high' | 'medium' | 'low';
  title: string;
  detail: string;
};

export type GbpHealth = {
  afNumber: string;
  placeId: string | null;
  displayName: string | null;
  primaryCategory: string | null;
  categoryCount: number;
  hasPhone: boolean;
  hasWebsite: boolean;
  hasHours: boolean;
  hasDescription: boolean;
  rating: number | null;
  reviewCount: number;
  mostRecentReviewDays: number | null;
  photoCount: number;
  businessStatus: string | null;
  score: number;
  actionItems: GbpHealthActionItem[];
  syncedAt: string | null;
  refreshing?: boolean;
};

export type RankingsMapPoint = {
  afNumber: string;
  name: string;
  location: string;
  fbcName: string | null;
  lat: number;
  lng: number;
  packCount: number;
  avgMapsRank: number | null;
  keywordsRanked: number;
};

export type RankingsNetworkSummary = {
  topPerformers: RankingsTopPerformer[];
  needsAttention: RankingsLaggard[];
  mapPoints: RankingsMapPoint[];
  locationsConsidered: number;
};

export type GbpNetworkSummary = {
  dateRange: string;
  locationsTotal: number;
  locationsWithData: number;
  metrics: {
    totalViews: GbpNetworkMetric;
    viewsMaps: GbpNetworkMetric;
    activity: GbpNetworkMetric;
    drivingDirections: GbpNetworkMetric;
    phoneCalls: GbpNetworkMetric;
  };
};

export type AnalyticsNetworkSummary = {
  dateRange: string;
  locationsTotal: number;
  locationsWithData: number;
  metrics: {
    sessions: GbpNetworkMetric;
    chats: GbpNetworkMetric;
    phoneClicks: GbpNetworkMetric;
    webformSubmissions: GbpNetworkMetric;
  };
};

export type AdsCampaign = { name: string; type: string; status: string };

export type AdsLocation = {
  clicks: number;
  impressions: number;
  conversions: number;
  cost: number;
  ctr: number;
  convRate: number;
  avgCPC: number;
  avgCPM: number;
  costPerConv: number;
  campaigns: AdsCampaign[];
  clicksChange: number | null;
  impressionsChange: number | null;
  conversionsChange: number | null;
  costChange: number | null;
  ctrChange: number | null;
  convRateChange: number | null;
  avgCPCChange: number | null;
  avgCPMChange: number | null;
  costPerConvChange: number | null;
};

export type AdsSheetResponse = {
  location: AdsLocation | null;
  dateRange: string;
};

export type MetaCreative = {
  adId: string;
  adName: string;
  status: string;
  thumbnailUrl: string | null;
  imageUrl: string | null;
  body: string | null;
  title: string | null;
  callToAction: string | null;
  instagramPermalinkUrl: string | null;
  facebookPermalinkUrl: string | null;
};

export type MetaCampaignSummary = {
  id: string;
  name: string;
  status: string;
  objective: string;
};

export type MetaInsights = {
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
  cpc: number;
  reach: number;
  frequency: number;
  conversions: number;
  convRate: number;
  costPerConv: number;
};

export type MetaResponse = {
  af_number: string;
  found: boolean;
  campaigns: MetaCampaignSummary[];
  current: MetaInsights;
  previous: MetaInsights;
  changes: {
    impressions: number | null;
    clicks: number | null;
    ctr: number | null;
    spend: number | null;
    cpc: number | null;
    conversions: number | null;
    convRate: number | null;
    costPerConv: number | null;
  };
  creatives: MetaCreative[];
  dateRange: string;
};

export type Call = {
  id: number;
  callrailId?: string;
  trackerId?: string;
  afNumber?: string;
  sourceAf?: string;
  customerName?: string | null;
  customerPhone?: string | null;
  customerCity?: string | null;
  customerState?: string | null;
  duration?: number;
  answered?: boolean;
  voicemail?: boolean;
  ivrDetected?: boolean;
  startTime: string;
  source?: string;
  sourceName?: string;
  trackingPhoneNumber?: string;
  callrailTags?: string[];
  callrailLeadStatus?: string | null;
  callrailLeadScore?: string | number | null;
  callrailSentiment?: string | null;
  callrailHighlights?: string[];
  speakerPercentAgent?: number | null;
  speakerPercentCustomer?: number | null;
  transcript?: string | null;
  callSummary?: string | null;
  hasRecording?: boolean;
  clientInference?: {
    patientName?: string | null;
    patientAge?: number | null;
    patientRelationship?: string | null;
    callerName?: string | null;
    callerRelationship?: string | null;
    patientCondition?: string | null;
    patientLocation?: string | null;
    careNeeds?: string | null;
    confidence?: number | null;
    modelUsed?: string;
    sourceUsed?: string;
  } | null;
  annotation?: {
    tags?: string[];
    leadStatus?: string;
    notes?: string;
    value?: number;
  } | null;
};

export type CallsListResponse = {
  data: Call[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
};

export type CallStats = {
  totalCalls: number;
  answeredCalls: number;
  missedCalls: number;
  voicemails: number;
  avgDuration: number;
  briefCalls: number;
  topSources: Array<{ source: string; count: number }>;
};

export type FunnelStage = {
  totalCalls?: number;
  answered?: number;
  qualityLeads?: number;
  scheduleRequested?: number;
  scheduleBooked?: number;
  assessmentsBooked?: number;
  interviewsScheduled?: number;
  avgDuration?: number;
  briefCalls?: number;
  missed?: number;
  answerRate?: number;
};

export type FunnelResponse = {
  current: FunnelStage;
  previous: FunnelStage | null;
};

export type TimeseriesPoint = {
  date: string;
  total: number;
  answered: number;
  missed: number;
  ivr: number;
};

export type CallSortField = 'start_time' | 'duration' | 'customer_name' | 'source' | 'af_number';

export type ScorecardRow = {
  afNumbers?: string[];
  afNumber: string;
  name: string;
  location?: string;
  fbcName?: string;
  totalCalls: number;
  answerRate: number;
  qualityLeads: number;
  qualityRate: number;
  booked: number;
  bookingRate: number;
  status: 'red' | 'yellow' | 'green';
  issue?: string;
  assessmentsBooked?: number;
  interviewsScheduled?: number;
  locations?: ScorecardRow[];
  // Satellite roll-up flags (computed server-side from Zoho + Yext).
  isSatellite?: boolean;
  parentAf?: string | null;
  hasOwnOffice?: boolean;
  sharedSatellites?: string[];
};

export type FbcPerformance = {
  fbcName: string;
  fbcZohoId?: string;
  franchiseeCount: number;
  totalCalls: number;
  answerRate: number;
  qualityRate: number;
  booked?: number;
  bookingRate: number;
  assessmentsBooked?: number;
  interviewsScheduled?: number;
};

export type TopBottomRow = {
  afNumber: string;
  name: string;
  location?: string;
  fbcName?: string;
  totalCalls: number;
  answerRate: number;
  booked: number;
  bookingRate: number;
  qualityRate: number;
  issue?: string;
};

export type TopBottomResponse = {
  top: TopBottomRow[];
  bottom: TopBottomRow[];
};

export type ContextView =
  | { kind: 'network' }
  | { kind: 'fbc'; fbcName: string }
  | { kind: 'franchisee'; afNumber: string };

export type GbpTopPerformer = { afNumber: string; name: string; count: number };

export type GbpPoorPerformer = {
  afNumber: string;
  name: string;
  count: number;
  daysOpen: number;
  openDate: string;
};

export type GbpReviewsSummary = {
  topPerformers: GbpTopPerformer[];
  poorPerformers: GbpPoorPerformer[];
};

export type OrganicUnderperformer = {
  afNumber: string;
  name: string;
  sessions: number;
  daysOpen: number;
  openDate: string;
};

export type WebsiteTrafficTopPerformer = {
  afNumber: string;
  name: string;
  sessions: number;
  daysOpen: number;
  openDate: string;
};

export type UserRow = {
  id: number | null;
  email: string;
  name: string | null;
  role: 'admin' | 'fbc' | 'franchisee';
  afNumber?: string | null;
  location?: string | null;
  fbcName?: string | null;
  createdAt?: string | null;
  lastLogin?: string | null;
  hasPassword?: boolean;
  afNumbers?: string[];
  pulseStatus: 'active' | 'pending' | 'not_registered';
  source?: string;
};

export type CallFilters = {
  search: string;
  status: '' | 'answered' | 'missed' | 'voicemail' | 'ivr';
  sentiment: '' | 'positive' | 'negative' | 'neutral';
  dateFrom: string;
  dateTo: string;
};

export type TabId =
  | 'tab-website'
  | 'tab-performance'
  | 'tab-paid'
  | 'tab-calls'
  | 'tab-admatch'
  | 'tab-social'
  | 'tab-welcomehome'
  | 'tab-content'
  | 'tab-links';

export const TABS: ReadonlyArray<{ id: TabId; label: string; ready: boolean }> = [
  { id: 'tab-website', label: 'Live Website & Reviews', ready: true },
  { id: 'tab-performance', label: 'Organic', ready: true },
  { id: 'tab-paid', label: 'Paid', ready: true },
  { id: 'tab-calls', label: 'Calls and Leads', ready: true },
  { id: 'tab-admatch', label: 'Ad Match Amount(s)', ready: true },
  { id: 'tab-social', label: 'Social', ready: true },
  { id: 'tab-welcomehome', label: 'WelcomeHome', ready: true },
  { id: 'tab-content', label: 'Content', ready: true },
  { id: 'tab-links', label: 'Important Links', ready: true },
];

// ── Content delivery ────────────────────────────────────────────────

export type ContentSubmissionStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'published'
  | 'publish_failed';

export type MyContentSubmission = {
  id: number;
  af_number: string;
  submission_type: 'news_events' | 'blog' | 'both';
  news_headline: string | null;
  blog_headline: string | null;
  status: ContentSubmissionStatus;
  created_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  published_at: string | null;
  published_url: string | null;
  publish_error: string | null;
  publish_at: string | null;
};

// Admin's view of a submission in the queue (no full content body — that's loaded on click).
export type AdminContentSubmissionRow = {
  id: number;
  af_number: string;
  marketing_name: string | null;
  site_url: string | null;
  submission_type: 'news_events' | 'blog' | 'both';
  news_headline: string | null;
  blog_headline: string | null;
  status: ContentSubmissionStatus;
  created_at: string;
  approved_at: string | null;
  published_at: string | null;
  publish_error: string | null;
  published_url: string | null;
  submitter_email: string | null;
  submitter_name: string | null;
  on_behalf: number | boolean; // mysql returns 1/0 for boolean expression
  approver_email: string | null;
  approver_name: string | null;
};

export type AdminContentSubmissionDetail = AdminContentSubmissionRow & {
  news_content: string | null;
  blog_content: string | null;
  blog_author: string | null;
  images: Array<{ url: string; name?: string; size?: number }>;
  submitting_on_behalf_user_id: number | null;
  rejection_reason: string | null;
  rejector_email: string | null;
  rejector_name: string | null;
  approved_by_user_id: number | null;
  rejected_at: string | null;
  rejected_by_user_id: number | null;
  published_post_id: number | null;
  published_url: string | null;
  publish_at: string | null;
  seo_metadata: { seoTitle: string; metaDesc: string; slug: string; summary?: string; showSummary?: boolean } | null;
  image_alts: Array<{ alt: string; title: string }> | null;
  // 1 when this franchisee has no previously *published* blog.
  is_first_blog?: number | boolean | null;
  // 1 when marketing has already been told this franchisee needs blog setup.
  blog_setup_acked?: number | boolean | null;
};

export type ContentPendingCountResponse = { count: number };

// ── WelcomeHome ─────────────────────────────────────────────────────

export type WhEffectivenessRow = {
  leadSourceId: number | null;
  leadSourceName: string;
  leadSourceCategory: string;
  leads: number;
  reachedAssessment: number;
  reachedIntake: number;
  reachedStartedService: number;
  closedLost: number;
  avgDaysToCare: number | null;
};

export type WhEffectivenessCommunity = {
  community_id: number;
  af_number: string;
  rows: WhEffectivenessRow[];
};

export type WhEffectivenessResponse = {
  af_number: string;
  window_days: number;
  communities: WhEffectivenessCommunity[];
  synced_at: string | null;
};

export type WhHygieneCommunity = {
  community_id: number;
  af_number: string;
  medianSpeedToLeadBhMinutes: number | null;
  medianSpeedToLeadRawMinutes: number | null;
  sampleSize: number;
  avgActivitiesThroughAssessment: number | null;
};

export type WhHygieneResponse = {
  af_number: string;
  window_days: number;
  communities: WhHygieneCommunity[];
  network: {
    medianSpeedToLeadBhMinutes: number | null;
    medianSpeedToLeadRawMinutes: number | null;
    avgActivitiesThroughAssessment: number | null;
  };
};

// ── Webform Leads (Ninja Forms) ─────────────────────────────────────
export type WebformLead = {
  id: number;
  submission_id: number;
  form_id: number | null;
  form_title: string | null;
  submitted_at: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;
  extra: Record<string, string> | null;
  site_url: string | null;
};

export type WebformLeadsResponse = {
  af_number: string;
  leads: WebformLead[];
  synced_at: string | null;
};
